/**
 * ServiceDispositionView (P396) -- the derived service disposition.
 * Route: /twin/service-disposition.
 *
 * A proposed TIME-style disposition for every service, derived from
 * live evidence rather than typed into a form.
 *
 * Grouped by disposition rather than listed flat, because that is how a
 * portfolio review is actually read: the services calling for action
 * first. `tolerate` is the large quiet majority (58 of 78 on the demo
 * estate), so it collapses behind a toggle -- present and countable,
 * but not burying the twenty that need a decision.
 */
import { useState } from "react";
import { Figure } from "shared/components/Figure";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { continuityPostureLabel, layerDetailLabel } from "shared/vocab/workflowState";

import { entityDetailPath } from "shared/routes/entityRoutes";

import { api } from "shared/api/client";
import { MarsadIcon } from "shared/components/MarsadIcon";

export interface DispositionRowOut {
  service_id: string;
  code: string;
  name: string;
  classification: string | null;
  lifecycle_stage: string | null;
  continuity_posture: string | null;
  rto_hours: number | null;
  supplier_count: number;
  supports_mvb: boolean;
  open_critical: number;
  open_high: number;
  disposition: "invest" | "migrate" | "eliminate" | "tolerate";
  /** F3: the same evidence as `drivers`, as keys + values, so it can be
   *  read in the reader's language with our enums through a catalogue. */
  driver_terms?: { key: string; params: Record<string, unknown> }[];
  rationale: string;
  drivers: string[];
}

export interface DispositionReportOut {
  summary: {
    services_total: number;
    by_disposition: Record<string, number>;
    mvb_supporting: number;
  };
  rows: DispositionRowOut[];
}

/** Action-first order. Matches the backend's DISPOSITION_RANK. */
const ORDER = ["invest", "migrate", "eliminate", "tolerate"] as const;

/**
 * F3 -- a driver's value read through the right vocabulary.
 *
 * Two of the six drivers carry one of our enums: a service
 * classification and a continuity posture. Disjoint sets, so one
 * helper resolves both and a value in neither falls through to the
 * de-underscored form rather than vanishing.
 */
function dispositionTerm(value: unknown): string {
  if (typeof value !== "string" || !value) return "";
  const posture = continuityPostureLabel(value);
  return posture || layerDetailLabel(value);
}

const TONE: Record<string, string> = {
  invest: "bg-rose-100 text-rose-800",
  migrate: "bg-amber-100 text-amber-800",
  eliminate: "bg-slate-200 text-slate-700",
  tolerate: "bg-emerald-100 text-emerald-800",
};

export function ServiceDispositionView() {
  const { t } = useTranslation();
  const [showTolerate, setShowTolerate] = useState(false);

  const report = useQuery({
    queryKey: ["twin", "service-disposition"],
    queryFn: () => api.get<DispositionReportOut>("/v1/twin/service-disposition"),
  });
  const p = report.data;
  const visible = showTolerate ? ORDER : ORDER.filter((d) => d !== "tolerate");

  return (
    <article data-testid="service-disposition-view" className="max-w-5xl space-y-5">
      <header className="space-y-1">
        <h1 className="font-display text-2xl text-slate-900">
          <MarsadIcon
            name="constellation"
            className="me-2 inline-block h-6 w-6 align-[-4px]"
          />
          {t("serviceDisposition.heading")}
        </h1>
        <p className="max-w-3xl text-sm text-slate-500">
          {t("serviceDisposition.body")}
        </p>
      </header>

      {report.isError && (
        <p className="text-sm text-rose-700">{t("serviceDisposition.error")}</p>
      )}

      {p && (
        <>
          <section
            data-testid="service-disposition-summary"
            className="flex flex-wrap items-center gap-2 text-sm"
          >
            <span className="rounded border border-paper-2 bg-white px-2 py-1">
              {t("serviceDisposition.total", { count: p.summary.services_total })}
            </span>
            {ORDER.map((d) => (
              <span
                key={d}
                data-testid={`service-disposition-count-${d}`}
                className={`rounded px-2 py-1 ${TONE[d]}`}
              >
                {/* D2 -- a count and a translated word side by side.
                    Reordered, the number followed the word instead of
                    leading it. The count is the figure; isolate it. */}
                <Figure>{p.summary.by_disposition[d] ?? 0}</Figure>{" "}
                {t(`serviceDisposition.${d}`)}
              </span>
            ))}
          </section>

          {ORDER.filter((d) => d !== "tolerate").every(
            (d) => (p.summary.by_disposition[d] ?? 0) === 0,
          ) && (
            <p className="text-sm text-slate-500">{t("serviceDisposition.noAction")}</p>
          )}

          {visible.map((d) => {
            const rows = p.rows.filter((r) => r.disposition === d);
            if (rows.length === 0) return null;
            return (
              <section key={d} data-testid={`service-disposition-group-${d}`}>
                <h2 className="mb-1 font-mono text-[11px] uppercase tracking-wide text-slate-500">
                  {t(`serviceDisposition.${d}`)} ({rows.length})
                </h2>
                <ul className="space-y-1.5">
                  {rows.map((r) => (
                    <li
                      key={r.service_id}
                      className="rounded border border-paper-2 bg-white p-2.5"
                    >
                      <div className="flex flex-wrap items-baseline gap-2">
                        <span className={`rounded px-1.5 py-0.5 text-xs ${TONE[d]}`}>
                          {t(`serviceDisposition.${d}`)}
                        </span>
                        {/* Round 10 F1 -- the rows became links this round
                            and every link was broken:
                            `/twin/entities/service/<id>` is a route that
                            exists nowhere in the product, which serves
                            `/twin/service/<id>`. One helper owns the
                            spelling now, and where it has no page to
                            offer the name is text rather than a link to
                            nothing. */}
                        {(() => {
                          const href = entityDetailPath("service", r.service_id);
                          return href ? (
                            <Link
                              to={href}
                              className="font-medium text-brand-brassDark hover:underline"
                            >
                              {r.name}
                            </Link>
                          ) : (
                            <span className="font-medium">{r.name}</span>
                          );
                        })()}
                        {r.supports_mvb && (
                          <span className="rounded bg-indigo-100 px-1.5 py-0.5 text-xs text-indigo-800">
                            {t("serviceDisposition.mvb")}
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-sm text-slate-700">{r.rationale}</p>
                      {/* Round 11 F3 -- the evidence, in the reader's
                          words. The server composed these in English
                          with our column values inside them --
                          "classified ibs_supporting", "continuity
                          posture active_passive" -- five of the same
                          family on a page that proposes spending
                          money, and no catalogue could reach any of
                          them because the sentence was already built.
                          The server sends the key and the values now;
                          `drivers` stays as the English fallback. */}
                      <p className="mt-1 flex flex-wrap gap-1">
                        {(r.driver_terms ?? []).length > 0
                          ? r.driver_terms!.map((d, i) => (
                              <span
                                key={`${d.key}-${i}`}
                                data-testid={`service-disposition-driver-${d.key}`}
                                className="rounded bg-paper-2 px-1.5 py-0.5 text-xs text-slate-600"
                              >
                                {t(`serviceDisposition.driver.${d.key}`, {
                                  ...d.params,
                                  term: dispositionTerm(d.params.term),
                                })}
                              </span>
                            ))
                          : r.drivers.map((dr) => (
                              <span
                                key={dr}
                                className="rounded bg-paper-2 px-1.5 py-0.5 text-xs text-slate-600"
                              >
                                {dr}
                              </span>
                            ))}
                      </p>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}

          <button
            type="button"
            data-testid="service-disposition-toggle-tolerate"
            onClick={() => setShowTolerate((v) => !v)}
            className="text-sm text-brand-brassDark hover:underline"
          >
            {showTolerate
              ? t("serviceDisposition.hideTolerate")
              : t("serviceDisposition.showTolerate", {
                  count: p.summary.by_disposition.tolerate ?? 0,
                })}
          </button>

          <p className="border-t border-paper-2 pt-3 text-xs text-slate-500">
            {t("serviceDisposition.boundary")}
          </p>
        </>
      )}
    </article>
  );
}
